import React, { useCallback, useMemo, useState } from "react";

import {
  ClockCircleOutlined,
  DownOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { Button } from "antd";

import SubscriptionCategoryUsage from "./SubscriptionCategoryUsage";
import UsageProgress from "./UsageProgress";
import UsageStatusLabel from "./UsageStatusLabel";

import { getSubscriptionTierUsage } from "../../utils";

import "./PlanTestUsage.css";

const CATEGORY_SHORT_TITLES = {
  MICRO: { key: "microbiology", title: "Microbiology", shortTitle: "Micro" },
  CHEM: { key: "chemistry", title: "Chemistry", shortTitle: "Chemi" },
  ENV: { key: "environment", title: "Environment", shortTitle: "Env" },
};

const tierMatchesCategoryPrefix = (tier, codePrefix) => {
  const code = tier.usage_category_code || tier.code || "";
  return String(code).toUpperCase().startsWith(codePrefix);
};

const sumSubscriptionTierUsage = (tiers = [], topupPlan = false) => (
  tiers.reduce((totals, tier) => {
    const usage = getSubscriptionTierUsage(tier, {
      billingMode: "subscription",
      topupPlan,
    });

    return {
      used: totals.used + usage.used,
      total: totals.total + usage.total,
    };
  }, { used: 0, total: 0 })
);

export default function PlanTestUsage({
  title = "Plan Credit Usage",
  items = [],
  used,
  total,
  disabled = false,
  statusLabel,
  renewsOn,
  nextResetInDays,
  billingCycle,
  note,
  categories = [],
  breakdownTopupPlan = false,
  breakdownToggleLabel = "View usage breakdown",
  breakdownCloseToggleLabel = "Close usage breakdown",
}) {
  const [breakdownOpen, setBreakdownOpen] = useState(true);
  const buildCategoryProgress = useCallback(() => (
    Object.entries(CATEGORY_SHORT_TITLES).flatMap(([codePrefix, group]) => {
      const tiers = categories.flatMap((category) => (
        (category.tiers || []).filter((tier) => tierMatchesCategoryPrefix(tier, codePrefix))
      ));
      if (!tiers.length) {
        return [];
      }

      const usage = sumSubscriptionTierUsage(tiers, breakdownTopupPlan);
      const colorSource = items.find((item) => item.key === group.key) || {};

      return [{
        key: group.key,
        title: group.title,
        shortTitle: group.shortTitle,
        value: usage.used,
        used: usage.used,
        total: usage.total,
        bgcolor: colorSource.bgcolor,
        segmentBg: colorSource.segmentBg,
      }];
    })
  ), [categories, items, breakdownTopupPlan]);
  const categoryProgress = useMemo(
    () => buildCategoryProgress(),
    [buildCategoryProgress],
  );
  const hasCategoryCredits = categoryProgress.length > 0;
  const usedCount = hasCategoryCredits
    ? categoryProgress.reduce((sum, item) => sum + item.used, 0)
    : (used ?? items.reduce((sum, item) => sum + Number(item.value || 0), 0));
  const totalCredits = hasCategoryCredits
    ? categoryProgress.reduce((sum, item) => sum + item.total, 0)
    : Number(total || 0);
  const progressItems = hasCategoryCredits ? categoryProgress : items;

  const resolvedStatus = statusLabel
    ?? (disabled ? "Exhausted" : "On Track- You're using your plan");

  const billingCycleLabel = String(billingCycle || "").trim().toLowerCase() || "monthly";
  const resolvedNote = note ?? (
    disabled
      ? "Your subscription credits are exhausted, you are currently using credits from your top-up pool."
      : `You're using your ${billingCycleLabel} credits allocation. Usage updates automatically as tests are submitted.`
  );

  const rootClass = [
    "PlanTestUsage",
    disabled ? "PlanTestUsage--disabled" : "",
  ].filter(Boolean).join(" ");

  return (
    <article className={rootClass}>
      <header className="PlanTestUsage__Header">
        <h2 className="PlanTestUsage__Title">{title}</h2>
        <div className="PlanTestUsage__MetricsRow">
          <div className="PlanTestUsage__Metrics">
            <span className="PlanTestUsage__Used">{usedCount.toLocaleString()}</span>
            <span className="PlanTestUsage__Capacity">
              {`used of ${totalCredits.toLocaleString()} credits`}
            </span>
            {resolvedStatus && (
              <UsageStatusLabel
                variant="plain"
                status={disabled ? "critical" : "healthy"}
                label={resolvedStatus}
              />
            )}
          </div>
          {(renewsOn || nextResetInDays != null) && (
            <p className="PlanTestUsage__Renewal">
              <ClockCircleOutlined aria-hidden />
              {renewsOn && (
                <span className="PlanTestUsage__RenewalText">{`Renews on ${renewsOn}`}</span>
              )}
              {renewsOn && nextResetInDays != null && (
                <span className="PlanTestUsage__RenewalDivider" aria-hidden />
              )}
              {nextResetInDays != null && (
                <span className="PlanTestUsage__RenewalText">
                  {`Next reset in ${nextResetInDays} days`}
                </span>
              )}
            </p>
          )}
        </div>
      </header>

      <UsageProgress items={progressItems} total={totalCredits} />

      <div className="PlanTestUsage__NoteContainer">
        {resolvedNote && (
          <p className="PlanTestUsage__Note">
            <InfoCircleOutlined />
            {resolvedNote}
          </p>
        )}
        <Button
          type="text"
          onClick={() => setBreakdownOpen((open) => !open)}
        >
          {breakdownOpen ? breakdownCloseToggleLabel : breakdownToggleLabel}
          {breakdownOpen ? <UpOutlined /> : <DownOutlined />}
        </Button>
      </div>

      {breakdownOpen && categories.length > 0 && (
        <div className="PlanTestUsage__Breakdown">
          <SubscriptionCategoryUsage
            title="Subscription Category Credit Usage"
            categories={categories}
            billingMode="subscription"
            topupPlan={breakdownTopupPlan}
            embedded
          />
        </div>
      )}
    </article>
  );
}
